import React from 'react';
import { Avatar, Box, Chip, Stack, Typography, alpha, useTheme } from '@mui/material';
import { Person as PersonIcon, Phone as PhoneIcon } from '@mui/icons-material';
import { formatCnic } from '../../utils/cnic';

/**
 * One guardian — name, relation, phone, CNIC, email, occupation, plus
 * role tags ("Fee payer", "Primary contact"). Used by the family drawer and
 * by the applicant drawer's guardians captured before the family is linked.
 */

export interface GuardianRowData {
  name: string;
  relation: string;
  phone?: string;
  cnic?: string;
  email?: string;
  occupation?: string;
}

interface Props {
  guardian: GuardianRowData;
  /** Short role labels shown next to the name. */
  tags?: string[];
  /** Captured on an application but not yet a guardian record. */
  pending?: boolean;
  /** Buttons on the right (edit, remove…). */
  actions?: React.ReactNode;
}

const GuardianRow: React.FC<Props> = ({ guardian: g, tags = [], pending, actions }) => {
  const theme = useTheme();
  const tone = pending ? theme.palette.warning : theme.palette.primary;
  return (
    <Stack direction="row" spacing={1.75} alignItems="flex-start" sx={{ py: 1.5, px: 2 }}>
      <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(tone.main, 0.1), color: pending ? tone.dark : tone.main }}>
        <PersonIcon fontSize="small" />
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" fontWeight={600}>{g.name}</Typography>
          <Chip size="small" label={g.relation} variant="outlined" sx={{ height: 18, fontSize: 10, textTransform: 'capitalize' }} />
          {tags.map(t => (
            <Chip key={t} size="small" label={t} sx={{ height: 18, fontSize: 10, bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.dark' }} />
          ))}
        </Stack>
        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <PhoneIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary">{g.phone || '—'}</Typography>
          </Stack>
          {g.cnic && <Typography variant="caption" color="text.disabled" fontFamily="monospace">CNIC {formatCnic(g.cnic)}</Typography>}
          {g.email && <Typography variant="caption" color="text.disabled">{g.email}</Typography>}
          {g.occupation && <Typography variant="caption" color="text.disabled">{g.occupation}</Typography>}
        </Stack>
      </Box>
      {actions && <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>{actions}</Stack>}
    </Stack>
  );
};

export default GuardianRow;
